import React, { Component, useMemo } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import camelCase from 'lodash/camelCase';
import union from 'lodash/union';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
  SEARCH_PAGE_COMPANY_VIEW_PARAM,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import {
  ButtonTabNavHorizontal,
  H1,
  H3,
  H5,
  ModalInMobile,
  Page,
  ResponsiveBackgroundImageContainer,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import ViewSelector from './ViewSelector';
import { getSuboptionsForOption } from '../../ducks/dimr.duck';
import { COUNTRIES_BY_LOCALE, LOCALE, getCurrentLocale } from '../../util/locale';
import {
  ARCHIVED_TABS,
  LISTING_CONDITION,
  LISTING_STATUS,
  PENDING_INTAKE_TABS,
  PENDING_REVIEW_TABS,
  PRODUCT_TYPE_QUERY_MAP,
  SELL_AS_IS_TABS,
} from '../../constants/listing';
import {
  getMarketplaceId,
  isMarketplaceRespare,
  isMarketplaceSecondLife,
  MARKETPLACE_ID,
  SEARCH_PAGE_TYPE,
} from '../../util/marketplace';
import {
  getListingLocalesForUserRole,
  getListingStatusForUserRole,
  isUserVerifiedAdmin,
  isUserVerifiedCompanyDealer,
} from '../../util/user';

import {
  getArchivedTabs,
  getBuyingSellAsIsTabs,
  getContractYearTabs,
  getForSaleTabs,
  getPendingIntakeTabs,
  getPendingReviewTabs,
  getSellAsIsTabs,
} from '../../util/tabConfigs';

import { types as sdkTypes } from '../../util/sdkLoader';
import { useEmbededMode } from '../../hooks/useEmbededMode';
import TopbarSearchForm from '../../components/Topbar/TopbarSearchForm/TopbarSearchForm';
import { useKioskMode } from '../../hooks/useKioskMode';
import KioskTopbar from './KioskTopbar/KioskTopbar';
import { useKioskSearchSubmit } from '../../hooks/useKioskSearchSubmit';

import css from './SearchPage.module.css';

const { LatLng, LatLngBounds } = sdkTypes;
const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

const getMainPanelHeaderTitle = ({
  intl,
  is2ndLife,
  isAuthenticated,
  searchPageType,
  searchParamsInURL,
  searchParams,
  isSellAsIsFilterActive,
  isArchivedFilterActive,
}) => {
  if (!is2ndLife) return '';

  // Public view (not logged in) – published
  if (!isAuthenticated) {
    return intl.formatMessage({ id: 'SearchPage.published.title' });
  }

  const { meta_status, pendingIntakeState, meta_isBuyerConfirmPending } = searchParamsInURL || {};

  // Pending intake sub-states (ending soon, expired…)
  if (meta_status === 'pendingIntake' && pendingIntakeState) {
    return intl.formatMessage({
      id: `SearchPage.pendingIntake.${pendingIntakeState}.title`,
    });
  }

  // Sell as is
  if (isSellAsIsFilterActive) {
    return intl.formatMessage({ id: 'SearchPage.sellAsIs.title' });
  }

  // Pending sold
  if (meta_isBuyerConfirmPending) {
    return intl.formatMessage({ id: 'SearchPage.pendingSold.title' });
  }

  // Marketplace
  if (searchPageType === SEARCH_PAGE_TYPE.SELL_AS_IS) {
    return intl.formatMessage({ id: 'SearchPage.marketplace.title' });
  }

  if (searchPageType === SEARCH_PAGE_TYPE.PUBLISHED) {
    return intl.formatMessage({ id: 'SearchPage.published.title' });
  }

  // Archived
  if (isArchivedFilterActive) {
    return intl.formatMessage({ id: 'SearchPage.archived.title' });
  }

  // My listings
  if (searchPageType === SEARCH_PAGE_TYPE.MY_LISTINGS) {
    if (searchParams?.meta_status) {
      return intl.formatMessage({
        id: `SearchPage.${searchParams.meta_status}.title`,
      });
    }

    return intl.formatMessage({ id: 'SearchPage.myListings.title' });
  }

  return '';
};

const getMainPanelHeaderDescription = ({
  intl,
  searchPageType,
  searchParamsInURL,
  isArchivedFilterActive,
  isAuthenticated,
}) => {
  const { meta_status, pendingIntakeState, sellAsIsState } = searchParamsInURL || {};

  if (!isAuthenticated) {
    return intl.formatMessage({ id: 'SearchPage.published.description' });
  }

  // Pending intake sub-states
  if (meta_status === 'pendingIntake' && pendingIntakeState) {
    return intl.formatMessage({
      id: `SearchPage.pendingIntake.${pendingIntakeState}.description`,
    });
  }

  // Sell as is sub-states
  if (meta_status === 'sellAsIs' && sellAsIsState) {
    return intl.formatMessage({
      id: `SearchPage.sellAsIs.${sellAsIsState}.description`,
    });
  }

  // Archived
  if (isArchivedFilterActive) {
    return intl.formatMessage({ id: 'SearchPage.archived.description' });
  }

  // Generic per meta_status
  if (meta_status) {
    return intl.formatMessage({
      id: `SearchPage.${meta_status}.description`,
    });
  }

  // Fallback per search page type
  if (searchPageType === SEARCH_PAGE_TYPE.SELL_AS_IS) {
    return intl.formatMessage({ id: 'SearchPage.sellAsIs.description' });
  }

  if (searchPageType === SEARCH_PAGE_TYPE.PUBLISHED) {
    return intl.formatMessage({ id: 'SearchPage.published.description' });
  }

  if (searchPageType === SEARCH_PAGE_TYPE.MY_LISTINGS) {
    return intl.formatMessage({ id: 'SearchPage.myListings.description' });
  }

  return '';
};
export class SearchPageComponent extends Component {
  getInitialBounds(currentUser) {
    if (currentUser && isUserVerifiedCompanyDealer(currentUser)) {
      const locale = currentUser.attributes.profile.publicData.locale;
      switch (locale) {
        case 'se':
          return new LatLngBounds(
            new LatLng(68.40409825, 25.61811126),
            new LatLng(58.45472351, 7.33368663)
          );
        case 'no':
          return new LatLngBounds(
            new LatLng(68.06510528, 20.4367617),
            new LatLng(57.97316726, 2.15233707)
          );
        default:
          return new LatLngBounds(
            new LatLng(72.20918417, 26.96404808),
            new LatLng(52.10752176, -0.81811936)
          );
      }
    }
    return new LatLngBounds(
      new LatLng(72.20918417, 26.96404808),
      new LatLng(52.10752176, -0.81811936)
    );
  }

  constructor(props) {
    super(props);

    const is2ndLife = isMarketplaceSecondLife(props.config);

    const { currentUser } = this.props;
    const initialBounds = is2ndLife ? this.getInitialBounds(currentUser) : null;

    const queryParams = validUrlQueryParamsFromProps(props);
    const selectedTabFromUrl = queryParams.tab;

    const shouldGroupInitially =
      is2ndLife &&
      (queryParams?.meta_status === 'pendingIntake' || queryParams?.status === 'pendingIntake');

    this.state = {
      isSearchMapOpenOnMobile: props.tab === 'map',
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      selectedArchivedTab: selectedTabFromUrl || ARCHIVED_TABS.BOUGHT,
      selectedPendingIntakeTab: selectedTabFromUrl || PENDING_INTAKE_TABS.ALL,
      selectedPendingIntakeYearTab: selectedTabFromUrl || 'all',
      selectedPendingReviewTab: selectedTabFromUrl || PENDING_REVIEW_TABS.ALL,
      selectedForSaleTab: selectedTabFromUrl || LISTING_STATUS.PUBLISHED,
      selectedSellAsIsTab: selectedTabFromUrl || SELL_AS_IS_TABS.FOR_SALE,
      isGrouped: !!shouldGroupInitially,
      bounds: initialBounds,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    this.handleTableSearchSubmit = this.handleTableSearchSubmit.bind(this);
  }

  componentDidMount() {
    const { history, routeConfiguration, currentPage, location, config } = this.props;

    const is2ndLife = isMarketplaceSecondLife(config);
    if (!is2ndLife) return;

    const { phase } = parse(location.search);

    let preselectedStatus;
    switch (phase) {
      case 'endoflease':
        preselectedStatus = 'pendingIntake';
        this.setState({ isGrouped: true });
        break;
      case 'refurbishment':
        preselectedStatus = 'pendingReview';
        break;
      case 'reselling':
        preselectedStatus = 'refurbished';
        break;
      default:
        preselectedStatus = null;
    }

    if (preselectedStatus) {
      const currentParams = parse(location.search);

      if (!currentParams.meta_status) {
        const newParams = { ...currentParams, meta_status: preselectedStatus };
        history.replace(
          createResourceLocatorString(currentPage, routeConfiguration, {}, newParams)
        );

        this.setState(prev => ({
          currentQueryParams: {
            ...prev.currentQueryParams,
            meta_status: preselectedStatus,
          },
        }));
      }
    }
  }

  // Toggles button state for grouping listings in table view
  toggleGrouped = () => {
    const { config, location } = this.props;

    const is2ndLife = isMarketplaceSecondLife(config);
    const isTableView = new URLSearchParams(location.search).get('view') === 'table';

    if (!(is2ndLife && isTableView)) return;

    this.setState(prev => ({ isGrouped: !prev.isGrouped }));
  };

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;

    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName(this.props.currentPage, routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    const currentPathWithoutLang = currentPath?.replace(`/${this.props.interfaceLang}`, '');

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPathWithoutLang === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};

      // parse query parameters, including a custom attribute named category
      const { address, bounds, mapSearch, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, listingFieldsConfig, defaultFiltersConfig, dropNonFilterParams),
      };

      history.push(createResourceLocatorString(this.props.currentPage, routes, {}, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config, currentPage } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    history.push(createResourceLocatorString(currentPage, routeConfiguration, {}, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, currentPage } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    const clearedParams = {
      ...queryParams,
      keywords: undefined, // resetuj keywords
    };

    history.push(createResourceLocatorString(currentPage, routeConfiguration, {}, clearedParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config, currentPage } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // If we are changing the view, and the view is different from 'map'.
        const shouldRemoveLocation = updatedURLParams.view && updatedURLParams.view !== 'map';

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address: shouldRemoveLocation ? null : address,
            bounds: shouldRemoveLocation ? null : bounds,
          },
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          history.replace(createResourceLocatorString(currentPage, routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, currentPage } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString(currentPage, routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll = e => {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  };

  handleOptionSelect = (option, subFilter, filterKey) => {
    // reset with initial values only if user's changing the status filter
    if (filterKey === 'status') {
      const is2ndLife = isMarketplaceSecondLife(this.props.config);
      const nextGrouped = is2ndLife && option === 'pendingIntake';

      this.setState({
        currentQueryParams: {},
        selectedArchivedTab: ARCHIVED_TABS.BOUGHT,
        selectedPendingIntakeTab: PENDING_INTAKE_TABS.ALL,
        selectedPendingReviewTab: PENDING_REVIEW_TABS.ALL,
        selectedForSaleTab: LISTING_STATUS.PUBLISHED,
        selectedSellAsIsTab: SELL_AS_IS_TABS.FOR_SALE,
        selectedPendingIntakeYearTab: 'all',
        isGrouped: nextGrouped,
      });
    }
  };

  handleTableSearchSubmit(values) {
    const { history, routeConfiguration, currentPage, location } = this.props;

    const currentView = new URLSearchParams(location.search).get('view') || 'table';

    const searchParams = {
      ...this.state.currentQueryParams,
      keywords: values?.keywords,
      view: currentView,
    };

    history.push(createResourceLocatorString(currentPage, routeConfiguration, {}, searchParams));
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      isAuthenticated,
      authInfoLoaded,
      currentPage,
      queryCompanyInProgress,
      queryCompanyError,
      company,
      searchPageType,
      history,
      routes,
      menuCounts,
      archivedCounts,
      forSaleCounts,
      buyingAsIsCounts,
      sellingAsIsCounts,
      contractExpirationCounts,
      isEmbededMode,
      isKioskMode,
      kioskSearchSubmit,
    } = this.props;

    const is2ndLife = isMarketplaceSecondLife(config);
    const isRespare = !is2ndLife && isMarketplaceRespare(config);

    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const isMyListingsSearch = searchPageType === SEARCH_PAGE_TYPE.MY_LISTINGS;

    const isArhivedFilterActive =
      isMyListingsSearch && decodeURIComponent(history.location.search).includes('archived');

    const isPendingIntakeFilterActive =
      isMyListingsSearch && decodeURIComponent(history.location.search).includes('pendingIntake');

    const isSetForRefurbishmentFilterActive =
      isMyListingsSearch &&
      decodeURIComponent(history.location.search).includes('setForRefurbishment');

    const isRefurbishedFilterActive =
      isMyListingsSearch && decodeURIComponent(history.location.search).includes('refurbished');

    const isEndingSoonOrExpired =
      isPendingIntakeFilterActive &&
      decodeURIComponent(history.location.search).includes('pendingIntakeState');

    const isPendingReviewFilterActive =
      isMyListingsSearch &&
      decodeURIComponent(history.location.search).includes('pendingReview') &&
      !decodeURIComponent(history.location.search).includes('asIs');

    const isForSaleFilterActive =
      isMyListingsSearch &&
      decodeURIComponent(history.location.search).includes('forSale') &&
      (decodeURIComponent(history.location.search).includes('published') ||
        decodeURIComponent(history.location.search).includes('sold'));

    const isSellAsIsFilterActive =
      isMyListingsSearch && decodeURIComponent(history.location.search).includes('sellAsIsState');

    const selectedStatusFilter = (() => {
      if (isPendingIntakeFilterActive) return 'myContracts';
      if (isPendingReviewFilterActive) return 'toBeInspected';
      if (isSetForRefurbishmentFilterActive) return 'toBeRefurbished';
      if (isRefurbishedFilterActive) return 'prepareForReselling';
      if (isForSaleFilterActive) return 'forSale';
      if (isArhivedFilterActive) return 'archived';

      return undefined;
    })();

    const mainPanelHeaderTitle = getMainPanelHeaderTitle({
      intl,
      is2ndLife,
      isAuthenticated,
      searchPageType,
      searchParamsInURL,
      searchParams,
      isSellAsIsFilterActive,
      isArchivedFilterActive: isArhivedFilterActive,
    });

    const mainPanelHeaderDescription = getMainPanelHeaderDescription({
      intl,
      searchPageType,
      searchParamsInURL,
      isArchivedFilterActive: isArhivedFilterActive,
      isAuthenticated,
    });

    const { currentUser } = this.props;
    const locationSearch = parse(location.search);
    const { pub_productType, phase } = locationSearch;

    const normalizedProductType =
      isMarketplaceSecondLife(config) &&
      pub_productType &&
      PRODUCT_TYPE_QUERY_MAP[pub_productType.toLowerCase()];

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );

    const isSellAsIsPage = searchPageType === SEARCH_PAGE_TYPE.SELL_AS_IS;

    const selectedView =
      validQueryParams.view ||
      (isRespare
        ? 'map'
        : is2ndLife && isSellAsIsPage
        ? 'map'
        : is2ndLife || !location.search.includes(SEARCH_PAGE_COMPANY_VIEW_PARAM)
        ? 'grid'
        : 'list');

    const contractYears = getContractYearTabs(
      this.state.selectedPendingIntakeYearTab,
      locationSearch,
      this.setState.bind(this),
      history,
      routes,
      selectedView,
      normalizedProductType
    );

    const sellAsIsTabs = getSellAsIsTabs(
      this.state.selectedSellAsIsTab,
      locationSearch,
      this.setState.bind(this),
      history,
      routes,
      selectedView,
      buyingAsIsCounts,
      sellingAsIsCounts,
      normalizedProductType
    );

    const filteredSellAsIsTabs =
      locationSearch?.sellAsIsState && locationSearch.sellAsIsState === 'selling'
        ? sellAsIsTabs.filter(tab => tab.id !== 'bought')
        : sellAsIsTabs.filter(tab => tab.id !== 'sold');

    const isMapView = selectedView === 'map';
    const isGridView = selectedView === 'grid';
    const isTableView = selectedView === 'table';
    const isListView = !isMapView && !isGridView && !isTableView;

    const TABLE_ALLOWED_FILTER_KEYS = isUserVerifiedAdmin(currentUser)
      ? ['status', 'dimrModel', 'contractEndOwnerCompanyId']
      : ['status', 'dimrModel'];

    const EMBEDED_ALLOWED_FILTER_KEYS = ['status', 'dimrModel', 'contractEndOwnerCompanyId'];

    const filterForTable = filters =>
      is2ndLife && isTableView
        ? filters.filter(f => TABLE_ALLOWED_FILTER_KEYS.includes(f.key))
        : filters;

    const filterForEmbededMode = filters => {
      return filters.filter(f => EMBEDED_ALLOWED_FILTER_KEYS.includes(f.key));
    };

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availablePrimaryFilters = [
      ...customPrimaryFilters,
      ...(!is2ndLife ? defaultFilters : []),
    ];
    const availableFilters = [
      ...customPrimaryFilters,
      ...defaultFilters,
      ...customSecondaryFilters,
    ].filter(f => f?.showConfig?.isVisible !== false);

    // filter Lease status options based on query parameter
    const allowedStatusOptions = (() => {
      switch (phase) {
        case 'endoflease':
          return ['pendingIntake', 'archived'];
        case 'refurbishment':
          return ['pendingReview', 'setForRefurbishment', 'archived'];
        case 'reselling':
          return ['refurbished', 'published', 'archived'];
        default:
          return null;
      }
    })();

    const filteredAvailableFilters = is2ndLife
      ? availableFilters.map(filter => {
          if (filter.key === 'status' && allowedStatusOptions) {
            return {
              ...filter,
              enumOptions: filter.enumOptions.filter(opt =>
                allowedStatusOptions.includes(opt.option)
              ),
            };
          }
          return filter;
        })
      : availableFilters;

    const tableFilters = is2ndLife && isTableView ? filterForTable(filteredAvailableFilters) : [];

    const embededFilters =
      is2ndLife && isEmbededMode ? filterForEmbededMode(filteredAvailableFilters) : [];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    // Selected aka active secondary filters
    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, customSecondaryFilters, [])
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };

    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    const viewSelector =
      isRespare || !location.search.includes(SEARCH_PAGE_COMPANY_VIEW_PARAM) ? (
        <ViewSelector
          selectedView={selectedView}
          onSelect={this.getHandleChangedValueFn(true)}
          onMobileMapClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
          config={config}
        />
      ) : null;

    const effectiveGrouped = is2ndLife && isTableView ? this.state.isGrouped : false;
    const processedListings = effectiveGrouped
      ? Object.values(
          listings.reduce((acc, l) => {
            const contractId = l.attributes?.publicData?.contractId || 'no-contract';
            if (!acc[contractId]) {
              acc[contractId] = { contractId, groupItems: [] };
            }
            acc[contractId].groupItems.push(l);
            return acc;
          }, {})
        )
      : listings;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        {(isRespare || (is2ndLife && !isEmbededMode && !isKioskMode)) && (
          <TopbarContainer
            className={topbarClasses}
            currentPage={currentPage}
            currentSearchParams={validQueryParams}
            company={company}
            queryCompanyInProgress={queryCompanyInProgress}
          />
        )}

        {is2ndLife && isKioskMode && <KioskTopbar onSearchSubmit={kioskSearchSubmit} />}

        <div className={classNames(css.container, { [css.embededModeContainer]: isEmbededMode })}>
          {!isMapView && !isTableView && !isEmbededMode && (
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              <div className={css.filterColumnContent}>
                {filteredAvailableFilters.map(item => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersAside.${item.key}`}
                      filterKey={item.key}
                      idPrefix="SearchFiltersAside"
                      className={css.filter}
                      rootClassName={item.key.includes('Grading') ? 'grading' : null}
                      config={item}
                      is2ndLife={is2ndLife}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={urlQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                      onOptionSelect={(option, subFilter, filterKey) =>
                        this.handleOptionSelect(option, subFilter, filterKey)
                      }
                      searchPageType={searchPageType}
                      menuCounts={menuCounts}
                    />
                  );
                })}
                <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </button>
              </div>
            </aside>
          )}
          <div
            className={classNames(css.searchResultContainer, {
              [css.tableSearchResultContainer]: isTableView,
              [css.searchResultContainer2ndLife]: is2ndLife,
            })}
          >
            {authInfoLoaded && !isAuthenticated && (
              <ResponsiveBackgroundImageContainer
                className={css.topBanner}
                imageClass={css.topBannerImage}
                image={config.branding.searchPageImage}
                childrenWrapperClassName={css.topBannerText}
                sizes="100%"
              >
                <H1>
                  {is2ndLife
                    ? intl.formatMessage({ id: 'SearchPage.titleContracts' })
                    : intl.formatMessage({ id: 'SearchPage.title' })}
                </H1>
                {!is2ndLife && (
                  <H3 as="h2">{intl.formatMessage({ id: 'SearchPage.description' })}</H3>
                )}
              </ResponsiveBackgroundImageContainer>
            )}
            {is2ndLife && company ? (
              <div className={css.companyBanner}>
                <H3 as="h2">
                  {`${company.name} ${intl
                    .formatMessage({ id: 'General.listings' })
                    .toLowerCase()}`}
                </H3>
              </div>
            ) : null}
            {queryCompanyError ? (
              <div className={classNames(css.companyBanner, css.error)}>
                {intl.formatMessage({ id: 'MembersAdminPage.companyFetchFailed' })}
              </div>
            ) : null}
            {is2ndLife && !isEmbededMode && (
              <SearchFiltersMobile
                className={css.searchFiltersMobileMap}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                noResultsInfo={noResultsInfo}
                isMapVariant
                title={mainPanelHeaderTitle}
                description={mainPanelHeaderDescription}
                showExportButton={is2ndLife && isMyListingsSearch}
                listingsToExport={is2ndLife && isMyListingsSearch ? listings : []}
                pagination={pagination}
                isAuthenticated={isAuthenticated}
              >
                {filteredAvailableFilters.map(config => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersMobile.${config.key}`}
                      idPrefix="SearchFiltersMobile"
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      onOptionSelect={(option, subFilter, filterKey) =>
                        this.handleOptionSelect(option, subFilter, filterKey)
                      }
                    />
                  );
                })}
              </SearchFiltersMobile>
            )}
            {(isRespare || (is2ndLife && !isEmbededMode)) && (
              <MainPanelHeader
                className={css.mainPanelMapVariant}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                // quickfix: don't display counts for ending soon and expired
                resultsCount={is2ndLife && isEndingSoonOrExpired ? null : totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                title={is2ndLife ? mainPanelHeaderTitle : null}
                description={is2ndLife ? mainPanelHeaderDescription : null}
                showExportButton={is2ndLife && isMyListingsSearch}
                isTableView={isTableView}
                listingsToExport={is2ndLife && isMyListingsSearch ? listings : []}
                onManageDisableScrolling={onManageDisableScrolling}
                intl={intl}
                pagination={pagination}
                contractYears={contractYears}
                sellAsIsTabs={
                  isSellAsIsPage && (
                    <ButtonTabNavHorizontal
                      className={css.tabs}
                      tabs={getBuyingSellAsIsTabs(
                        this.state.selectedSellAsIsTab,
                        this.setState.bind(this),
                        history,
                        routes,
                        selectedView,
                        buyingAsIsCounts,
                        normalizedProductType
                      )}
                    />
                  )
                }
                tableSearch={
                  is2ndLife &&
                  isTableView && (
                    <div className={css.tableSearchWrapper}>
                      <TopbarSearchForm
                        onSubmit={this.handleTableSearchSubmit}
                        initialValues={{ keywords: searchParams?.keywords || '' }}
                        appConfig={config}
                        isTableView={isTableView}
                        wrapperClass={css.tableSearch}
                        isAuthenticated={isAuthenticated}
                      />
                    </div>
                  )
                }
                tableViewSection={
                  is2ndLife &&
                  isTableView &&
                  tableFilters &&
                  tableFilters?.length > 0 && (
                    <div>
                      <div className={css.tableFilters}>
                        {tableFilters.map(item => {
                          return (
                            <FilterComponent
                              isTableView={isTableView}
                              key={`SearchFiltersAside.${item.key}`}
                              filterKey={item.key}
                              idPrefix="SearchFiltersAside"
                              className={css.filter}
                              rootClassName={'tableViewFilters'}
                              config={item}
                              is2ndLife={is2ndLife}
                              marketplaceCurrency={marketplaceCurrency}
                              urlQueryParams={urlQueryParams}
                              initialValues={initialValues(
                                this.props,
                                this.state.currentQueryParams
                              )}
                              getHandleChangedValueFn={this.getHandleChangedValueFn}
                              intl={intl}
                              liveEdit
                              showAsPopup={false}
                              isDesktop
                              onOptionSelect={(option, subFilter, filterKey) =>
                                this.handleOptionSelect(option, subFilter, filterKey)
                              }
                              searchPageType={searchPageType}
                              menuCounts={menuCounts}
                            />
                          );
                        })}
                      </div>

                      <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                        <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                      </button>

                      <div className={css.groupToggleWrapper}>
                        {this.state.isGrouped ? (
                          <FormattedMessage id="TableView.ungroup" />
                        ) : (
                          <FormattedMessage id="TableView.group" />
                        )}

                        <label className={css.toggle}>
                          <input
                            type="checkbox"
                            checked={this.state.isGrouped}
                            onChange={this.toggleGrouped}
                          />
                          <span className={css.slider}></span>
                        </label>
                      </div>
                    </div>
                  )
                }
                isAuthenticated={isAuthenticated}
              >
                <div className={css.primaryFiltersWrapper}>
                  {is2ndLife && isArhivedFilterActive && (
                    <ButtonTabNavHorizontal
                      className={css.tabs}
                      tabs={getArchivedTabs(
                        this.state.selectedArchivedTab,
                        this.setState.bind(this),
                        history,
                        routes,
                        selectedView,
                        archivedCounts,
                        currentUser,
                        normalizedProductType
                      )}
                    />
                  )}
                  {is2ndLife &&
                    isPendingIntakeFilterActive &&
                    (isEndingSoonOrExpired ? (
                      <ButtonTabNavHorizontal
                        className={css.tabs}
                        tabs={getPendingIntakeTabs(
                          this.state.selectedPendingIntakeTab,
                          locationSearch,
                          this.setState.bind(this),
                          history,
                          routes,
                          selectedView,
                          contractExpirationCounts,
                          menuCounts,
                          normalizedProductType
                        )}
                      />
                    ) : (
                      <ButtonTabNavHorizontal className={css.tabs} tabs={contractYears} />
                    ))}
                  {is2ndLife &&
                    isUserVerifiedCompanyDealer(currentUser) &&
                    isPendingReviewFilterActive && (
                      <ButtonTabNavHorizontal
                        className={css.tabs}
                        tabs={getPendingReviewTabs(
                          this.state.selectedPendingReviewTab,
                          this.setState.bind(this),
                          history,
                          routes,
                          currentUser,
                          selectedView,
                          normalizedProductType
                        )}
                      />
                    )}
                  {is2ndLife && isForSaleFilterActive && (
                    <ButtonTabNavHorizontal
                      className={css.tabs}
                      tabs={getForSaleTabs(
                        this.state.selectedForSaleTab,
                        this.setState.bind(this),
                        history,
                        routes,
                        selectedView,
                        forSaleCounts,
                        normalizedProductType
                      )}
                    />
                  )}
                  {is2ndLife && isSellAsIsFilterActive && (
                    <ButtonTabNavHorizontal className={css.tabs} tabs={filteredSellAsIsTabs} />
                  )}
                  {isMapView && (
                    <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                      {availablePrimaryFilters.map(config => {
                        return (
                          <FilterComponent
                            key={`SearchFiltersPrimary.${config.key}`}
                            idPrefix="SearchFiltersPrimary"
                            config={config}
                            marketplaceCurrency={marketplaceCurrency}
                            urlQueryParams={validQueryParams}
                            initialValues={initialValues(this.props, this.state.currentQueryParams)}
                            getHandleChangedValueFn={this.getHandleChangedValueFn}
                            intl={intl}
                            showAsPopup
                            contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                          />
                        );
                      })}
                    </SearchFiltersPrimary>
                  )}
                  {!isTableView && viewSelector}
                </div>
              </MainPanelHeader>
            )}

            {is2ndLife && isEmbededMode && (
              <div className={css.embededModeFiltersSection}>
                {embededFilters.map(item => {
                  return (
                    <FilterComponent
                      isTableView={isTableView}
                      key={`SearchFiltersAside.${item.key}`}
                      filterKey={item.key}
                      idPrefix="SearchFiltersAside"
                      className={css.filter}
                      rootClassName={'tableViewFilters'}
                      config={item}
                      is2ndLife={is2ndLife}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={urlQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                      onOptionSelect={(option, subFilter, filterKey) =>
                        this.handleOptionSelect(option, subFilter, filterKey)
                      }
                      searchPageType={searchPageType}
                      menuCounts={menuCounts}
                    />
                  );
                })}
              </div>
            )}
            {isSecondaryFiltersOpen ? (
              <div className={classNames(css.searchFiltersPanel)}>
                <SearchFiltersSecondary
                  urlQueryParams={validQueryParams}
                  listingsAreLoaded={listingsAreLoaded}
                  applyFilters={this.applyFilters}
                  cancelFilters={this.cancelFilters}
                  resetAll={this.resetAll}
                  onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                >
                  {customSecondaryFilters.map(config => {
                    return (
                      <FilterComponent
                        key={`SearchFiltersSecondary.${config.key}`}
                        idPrefix="SearchFiltersSecondary"
                        config={config}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersSecondary>
              </div>
            ) : (
              <div
                className={classNames(css.listingsForMapVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={processedListings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  setActiveListing={onActivateListing}
                  isMapVariant
                  isListView={isListView}
                  isTableView={isTableView}
                  isGrouped={effectiveGrouped}
                  intl={intl}
                  selectedStatusFilter={selectedStatusFilter}
                  isAuthenticated={isAuthenticated}
                />
              </div>
            )}
          </div>
          {isMapView && (
            <ModalInMobile
              className={css.mapPanel}
              id={`${currentPage}.map`}
              isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
              onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.mapWrapper} data-testid="searchMapContainer">
                {shouldShowSearchMap ? (
                  <SearchMap
                    reusableContainerClassName={css.map}
                    activeListingId={activeListingId}
                    bounds={bounds || this.state.bounds}
                    center={origin}
                    isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                    location={location}
                    listings={listings || []}
                    onMapMoveEnd={this.onMapMoveEnd}
                    onCloseAsModal={() => {
                      onManageDisableScrolling(`${currentPage}.map`, false);
                    }}
                    messages={intl.messages}
                  />
                ) : null}
              </div>
            </ModalInMobile>
          )}
        </div>
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  tab: 'listings',
  activeListingId: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  tab: oneOf(['filters', 'listings', 'map']).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();

  const locale = getCurrentLocale();

  const marketplaceId = getMarketplaceId(config);

  const {
    searchParams,
    dimr,
    currentUser,
    searchPageType = SEARCH_PAGE_TYPE.MY_LISTINGS,
    currentPage = 'LandingPage',
    companies,
    models,
    fetchModelsInProgress,
    fetchModelsError,
    isAuthenticated,
  } = props;

  const { pub_category, pub_subcategory } = searchParams || {};

  const isSellAsIsSearch = searchPageType === SEARCH_PAGE_TYPE.SELL_AS_IS;
  const isPublishedSearch = searchPageType === SEARCH_PAGE_TYPE.PUBLISHED;

  const isCompanyView = location.search.includes(SEARCH_PAGE_COMPANY_VIEW_PARAM);

  const listingFieldsConfig = useMemo(() => {
    if (!config?.listing?.listingFields) return;

    return config.listing.listingFields.map(field => {
      const { key, marketplaceIds } = field;

      if (marketplaceIds && !marketplaceIds.includes(marketplaceId)) return null;

      const newField = { ...field };

      switch (key) {
        case 'category':
          newField.enumOptions = dimr.categories.map(category => ({
            option: category,
            label: `General.${camelCase(category)}`,
          }));

          break;

        case 'subcategory': {
          let subcategories = [];

          if (!pub_category)
            subcategories = getSuboptionsForOption(dimr.subcategories, pub_category);
          else {
            const splitCategories = pub_category.split(',');
            const splitSubcategories = pub_subcategory ? pub_subcategory.split(',') : [];

            // If we have already selected subcategories, but they are
            // not shown because of the selected categories, add them
            splitCategories.forEach(category => {
              subcategories = union(
                subcategories,
                getSuboptionsForOption(dimr.subcategories, category),
                splitSubcategories
              );
            });
          }

          newField.enumOptions = subcategories.map(category => ({
            option: category,
            label: `General.${camelCase(category)}`,
          }));

          break;
        }

        case 'itemType':
          newField.enumOptions = getSuboptionsForOption(dimr.itemTypes, pub_subcategory).map(
            type => ({
              option: type,
              label: `General.${camelCase(type)}`,
            })
          );
          break;

        case 'condition': {
          const isBrazilLocale = locale === LOCALE.BRAZIL;

          if (isBrazilLocale) return null;

          const isUSLocale = locale === LOCALE.UNITED_STATES;

          if (isUSLocale) {
            newField.enumOptions = [
              { option: LISTING_CONDITION.GRADE_A, label: 'General.gradeALongUS' },
              { option: LISTING_CONDITION.GRADE_B, label: 'General.gradeBLongUS' },
              { option: LISTING_CONDITION.GRADE_C, label: 'General.gradeCLongUS' },
              { option: LISTING_CONDITION.GRADE_D, label: 'General.gradeDLongUS' },
            ];
          } else {
            newField.enumOptions = newField.enumOptions.filter(
              e => e.option !== LISTING_CONDITION.GRADE_D
            );
          }

          break;
        }

        case 'country': {
          const countries = COUNTRIES_BY_LOCALE[locale] || [];

          if (countries.length < 1) {
            return null;
          }

          newField.enumOptions = countries.map(country => ({ option: country, label: country }));

          break;
        }

        // =========== 2ndlife fields ===========
        case 'status': {
          // These search pages will have only single status showing
          if (isSellAsIsSearch || isPublishedSearch || !isAuthenticated) return null;

          const roleStatuses = getListingStatusForUserRole(currentUser);

          // Admin should see all sell-as-is products
          // Dealer should see only selling sell-as-is products
          newField.enumOptions = newField.enumOptions.filter(o => {
            if (o.option === LISTING_STATUS.SELL_AS_IS && !isUserVerifiedAdmin(currentUser)) {
              return false;
            }
            return roleStatuses.includes(o.option);
          });

          newField.enumOptions = newField.enumOptions.filter(o => {
            if (o.option === LISTING_STATUS.SELLING && !isUserVerifiedCompanyDealer(currentUser)) {
              return false;
            }
            return roleStatuses.includes(o.option);
          });

          newField.enumOptions = newField.enumOptions.filter(o => roleStatuses.includes(o.option));

          break;
        }
        case 'contractEndOwnerCompanyId': {
          if (isCompanyView || isMarketplaceRespare(config)) return null;

          newField.enumOptions = companies?.map(company => ({
            option: company._id,
            label: company.name,
            isTranslatedLabel: true,
          }));
          newField.isOpenInitial = false;

          if (!newField.enumOptions?.length) return null;

          break;
        }

        case 'dimrModel': {
          if (isMarketplaceRespare(config)) return null;

          if (fetchModelsError) return null;

          if (fetchModelsInProgress) {
            newField.enumOptions = [{ option: '', label: 'General.loading' }];
          } else {
            newField.enumOptions = models?.map(model => ({
              option: model,
              label: model,
              isTranslatedLabel: true,
            }));
          }

          newField.isOpenInitial = false;

          break;
        }

        case 'inputGrading': {
          if (isMarketplaceRespare(config)) return null;

          newField.isOpenInitial = false;

          if (!newField.enumOptions?.length) return null;

          break;
        }

        case 'outputGrading': {
          if (isMarketplaceRespare(config)) return null;

          newField.isOpenInitial = false;

          if (!newField.enumOptions?.length) return null;

          break;
        }

        case 'husqvarnaCertified': {
          if (isMarketplaceRespare(config)) return null;

          newField.isOpenInitial = false;

          break;
        }

        // ========= both 2ndlife and respare
        case 'locale': {
          // We don't show filter on respare, even though its sent via request
          if (marketplaceId === MARKETPLACE_ID.RESPARE) return null;

          const roleLocales = getListingLocalesForUserRole(currentUser);

          newField.enumOptions = newField.enumOptions.filter(o => roleLocales.includes(o.option));
          newField.isOpenInitial = false;

          if (!newField.enumOptions.length) return null;

          break;
        }

        default:
          break;
      }

      return newField;
    });
  }, [
    config,
    dimr,
    locale,
    pub_category,
    pub_subcategory,
    marketplaceId,
    currentUser,
    isSellAsIsSearch,
    isPublishedSearch,
    companies,
    isCompanyView,
    models,
    fetchModelsError,
    fetchModelsInProgress,
    isAuthenticated,
  ]);

  const newConfig = useMemo(
    () => ({
      ...config,
      listing: { ...(config?.listing || {}), listingFields: listingFieldsConfig?.filter(Boolean) },
    }),
    [config, listingFieldsConfig]
  );

  const isEmbededMode = useEmbededMode();
  const isKioskMode = useKioskMode();
  const kioskSearchSubmit = useKioskSearchSubmit();

  return (
    <SearchPageComponent
      config={newConfig}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      routes={routes}
      {...props}
      currentPage={currentPage}
      searchPageType={searchPageType}
      isEmbededMode={isEmbededMode}
      isKioskMode={isKioskMode}
      kioskSearchSubmit={kioskSearchSubmit}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    queryCompanyInProgress,
    queryCompanyError,
    company,
    menuCounts,
    archivedCounts,
    forSaleCounts,
    buyingAsIsCounts,
    sellingAsIsCounts,
    contractExpirationCounts,
    models,
    fetchModelsInProgress,
    fetchModelsError,
  } = state.SearchPage;

  const { isAuthenticated, authInfoLoaded } = state.auth;
  const { interfaceLang } = state.ui;
  const { queryCompaniesInProgress, queryCompaniesError, companies } = state.company;

  const { currentUser } = state.user;

  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    isAuthenticated,
    authInfoLoaded,
    dimr: state.dimr,
    interfaceLang,

    queryCompanyInProgress,
    queryCompanyError,
    company,
    queryCompaniesInProgress,
    queryCompaniesError,
    companies,
    models,
    fetchModelsInProgress,
    fetchModelsError,
    menuCounts,
    archivedCounts,
    forSaleCounts,
    buyingAsIsCounts,
    sellingAsIsCounts,
    contractExpirationCounts,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
